import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { CommonUtils } from '../../(common-lib)/CommonUtils';
import { GooglePlacesService } from './GooglePlacesService';
import type { PlaceDetails, Suggestion, USState } from './types';

type OnSelectHandler = (details: PlaceDetails, suggestion: Suggestion) => void | Promise<void>;

export interface UsePlacesAutocompleteOptions {
  debounceDelay?: number;
  onSelect?: OnSelectHandler;
}

export interface UsePlacesAutocompleteReturn {
  suggestions: Suggestion[];
  showSuggestions: boolean;
  isLoading: boolean;
  isGoogleMapsLoaded: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  suggestionsRef: RefObject<HTMLDivElement | null>;
  requestSuggestions: (input: string, stateHint?: USState) => void;
  selectSuggestion: (suggestion: Suggestion) => Promise<PlaceDetails | void>;
  openIfHasSuggestions: () => void;
  hideSuggestions: () => void;
}

export function usePlacesAutocomplete(
  options: UsePlacesAutocompleteOptions = {}
): UsePlacesAutocompleteReturn {
  const { debounceDelay = 300, onSelect } = options;

  const placesService = GooglePlacesService.getInstance();

  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const requestSequenceRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDebounceTimer = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const cancelActiveRequest = useCallback(() => {
    clearDebounceTimer();
    requestSequenceRef.current += 1;
  }, [clearDebounceTimer]);

  useEffect(() => {
    let isMounted = true;

    const loadGoogleMaps = async () => {
      try {
        const loaded = await placesService.loadGoogleMapsAPI();
        if (isMounted) {
          setIsGoogleMapsLoaded(loaded);
        }
      } catch (error) {
        CommonUtils.logError(
          '[Address][usePlacesAutocomplete.loadGoogleMapsAPI] Failed to initialize Google Maps API.',
          error
        );
        if (isMounted) {
          setIsGoogleMapsLoaded(false);
        }
      }
    };

    if (placesService.isGoogleMapsLoaded()) {
      setIsGoogleMapsLoaded(true);
      return () => {
        isMounted = false;
      };
    }

    void loadGoogleMaps();

    return () => {
      isMounted = false;
    };
  }, [placesService]);

  const requestSuggestions = useCallback((input: string, stateHint?: USState) => {
    clearDebounceTimer();

    const requestId = ++requestSequenceRef.current;
    const trimmedInput = (input || '').trim();
    if (trimmedInput.length < 3 || !isGoogleMapsLoaded) {
      setIsLoading(false);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    setShowSuggestions(true);

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const nextSuggestions = await placesService.fetchSuggestions(trimmedInput, stateHint);
        if (requestId !== requestSequenceRef.current) return;

        setSuggestions(nextSuggestions);
        setShowSuggestions(true);
      } catch (error) {
        CommonUtils.logError(
          '[Address][usePlacesAutocomplete.requestSuggestions] Failed to fetch autocomplete suggestions.',
          error
        );
        if (requestId !== requestSequenceRef.current) return;

        setSuggestions([]);
        setShowSuggestions(true);
      } finally {
        if (requestId === requestSequenceRef.current) {
          setIsLoading(false);
        }
      }
    }, debounceDelay);
  }, [clearDebounceTimer, debounceDelay, isGoogleMapsLoaded, placesService]);

  const selectSuggestion = useCallback(async (suggestion: Suggestion): Promise<PlaceDetails | void> => {
    cancelActiveRequest();
    setShowSuggestions(false);
    setIsLoading(true);

    try {
      const details = await placesService.getPlaceDetails(suggestion);
      if (onSelect) {
        await onSelect(details, suggestion);
      }
      return details;
    } catch (error) {
      CommonUtils.logError(
        '[Address][usePlacesAutocomplete.selectSuggestion] Failed to fetch place details.',
        error
      );
      return;
    } finally {
      setIsLoading(false);
    }
  }, [cancelActiveRequest, onSelect, placesService]);

  const openIfHasSuggestions = useCallback(() => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  }, [suggestions.length]);

  const hideSuggestions = useCallback(() => {
    cancelActiveRequest();
    setIsLoading(false);
    setShowSuggestions(false);
  }, [cancelActiveRequest]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)
        && inputRef.current && !inputRef.current.contains(event.target as Node)
      ) {
        hideSuggestions();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      cancelActiveRequest();
    };
  }, [cancelActiveRequest, hideSuggestions]);

  return {
    suggestions,
    showSuggestions,
    isLoading,
    isGoogleMapsLoaded,
    inputRef,
    suggestionsRef,
    requestSuggestions,
    selectSuggestion,
    openIfHasSuggestions,
    hideSuggestions,
  };
}
